import { createBoardSyncStateMachineV2 } from '@canvas/connection-state/board-sync-v2';
import { type ConnectionStatus } from '@canvas/model/network';

export type ContentSyncState = {
  id?: string;
  connectionStatus?: ConnectionStatus;
  readOnly: boolean;
  pendingChanges: boolean;
};

export function syncContentStateChanges({
  state,
  contentSyncStateMachine,
  prevState,
}: {
  state: ContentSyncState;
  contentSyncStateMachine: ReturnType<typeof createBoardSyncStateMachineV2>;
  prevState: ContentSyncState;
}) {
  // don't show messages when user switched to a different board
  if (state.id !== prevState.id) {
    prevState.id = state.id;
    return;
  }

  // When user's access was downgraded
  // while they were offline and making changes,
  // as they come online, those changes get discarded
  // and we should notify them
  if (shouldSendChangesDiscardedMessage(state)) {
    contentSyncStateMachine.send({
      type: 'messagesDiscarded',
    });
    // When user's access was changed we should notify them
    // when they are online
  } else if (shouldSendPermissionsChangedMessage(state, prevState.readOnly)) {
    // notify user that their access was downgraded
    if (state.readOnly) {
      contentSyncStateMachine.send({
        type: 'editToViewAccess',
      });
      // notify user that their access was upgraded
    } else {
      contentSyncStateMachine.send({
        type: 'viewToEditAccess',
      });
    }

    prevState.readOnly = state.readOnly;
    // We should notify a user about syncing their changes
    // only when they have write access
    // and the pending changes state is updated
  } else if (shouldSendPendingChangesMessage(state, prevState.pendingChanges)) {
    // We mark pending changes regardless of the connection status
    if (state.pendingChanges) {
      contentSyncStateMachine.send({
        type: 'messagesPending',
      });
    }
    // We only mark changes as saved when a user is online
    if (!state.pendingChanges && state.connectionStatus === 'online') {
      contentSyncStateMachine.send({
        type: 'messagesSaved',
      });
    }

    prevState.pendingChanges = state.pendingChanges;
  }
  contentSyncStateMachine.execute();
}

const shouldSendChangesDiscardedMessage = (state: ContentSyncState) =>
  state.readOnly && state.connectionStatus === 'online' && state.pendingChanges;

const shouldSendPermissionsChangedMessage = (
  state: ContentSyncState,
  prevReadOnly?: boolean
) => state.readOnly !== prevReadOnly && state.connectionStatus === 'online';

const shouldSendPendingChangesMessage = (
  state: ContentSyncState,
  prevPendingChanges?: boolean
) => state.pendingChanges !== prevPendingChanges && !state.readOnly;
